// ETID (email template / campaign attribution id) helpers for app.tachusa.com.
//
// The marketing site (tachusa.com) writes ETID to a cookie scoped to
// `.tachusa.com`, which IS readable here on app.tachusa.com (unlike
// localStorage, which does not cross origins). This lets the app recover
// attribution even when ETID is missing from the login URL.

import { safeGetItem, safeSetItem } from './safeStorage';

export const ETID_STORAGE_KEY = 'etid';
export const ETID_COOKIE_NAME = 'tach_etid';

// 30 days — signups frequently complete in a later session than the ad click.
const ETID_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const getEtidCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${ETID_COOKIE_NAME}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.split('=')[1] ?? '');
  return value.length > 0 ? value : null;
};

export const setEtidCookie = (etid: string) => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  // Scope to the registrable domain so the cookie is shared with the
  // marketing site (tachusa.com) — writable from app.tachusa.com.
  const isProduction = hostname.endsWith('tachusa.com');
  const domain = isProduction ? '; domain=.tachusa.com' : '';
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; secure'
      : '';
  document.cookie = `${ETID_COOKIE_NAME}=${encodeURIComponent(
    etid
  )}; path=/; max-age=${ETID_COOKIE_MAX_AGE}${domain}; samesite=lax${secure}`;
};

// Remove the cross-domain cookie (mirrors setEtidCookie's scoping). Without
// this, logout clearing localStorage is ineffective: login.tsx falls back to
// the cookie and re-persists the previous user's attribution.
export const clearEtidCookie = () => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isProduction = hostname.endsWith('tachusa.com');
  const domain = isProduction ? '; domain=.tachusa.com' : '';
  document.cookie = `${ETID_COOKIE_NAME}=; path=/; max-age=0${domain}; samesite=lax`;
};

// Persist ETID to BOTH the cross-domain cookie and localStorage so it survives
// no matter which mechanism a later step reads from.
export const persistEtid = (etid: string) => {
  if (!etid) return;
  setEtidCookie(etid);
  safeSetItem(ETID_STORAGE_KEY, etid);
};

// The single ETID reader: URL param, then the cross-domain cookie, then the
// same-origin localStorage mirror.
//
// The precedence matters and the redundancy is not decorative. localStorage is
// the channel most likely to be missing — blocked site data, a full store, or
// Safari's ITP clearing script-writable storage — while the `.tachusa.com`
// cookie survives independently. Reading only one channel silently drops
// attribution for anyone whose other channel is the live one.
export const getEtid = (searchParamEtid?: string | null): string | null => {
  if (searchParamEtid) return searchParamEtid;
  return getEtidCookie() || safeGetItem(ETID_STORAGE_KEY);
};
